import { useEffect } from 'react';
import { type UseBoundStore, type StoreApi } from 'zustand';

import { devicesCheck } from '@ctct/infrastructure-layer';

export enum SessionStorageKeys {
  buynow_zustand_store = 'buynow_zustand_store',
  payflow_v2_trialer_zustand_store = 'payflow_v2_trialer_zustand_store',
  payflow_v2_customer_zustand_store = 'payflow_v2_customer_zustand_store',
  payflow_v2_suspended_zustand_store = 'payflow_v2_suspended_zustand_store',
  select_plan_zustand_store = 'select_plan_zustand_store',
}

const generateSessionStorageKey = (key: SessionStorageKeys, accountId?: number) => `${key}-${accountId || ''}`;

export const useSessionStorageZustandBeforeUnload = <
  storeType extends object,
  serializedType extends object = storeType
>(
  key: SessionStorageKeys,
  accountId: number | undefined,
  useStore: UseBoundStore<StoreApi<storeType>>,
  transformOnSave?: (state: storeType) => serializedType,
  transformOnLoad?: (state: serializedType) => storeType
) => {
  useEffect(() => {
    const saveToSessionStorage = (data: unknown) => {
      sessionStorage.setItem(generateSessionStorageKey(key, accountId), JSON.stringify(data));
    };

    const loadFromSessionStorage = () => {
      const storedData = sessionStorage.getItem(generateSessionStorageKey(key, accountId));

      return storedData ? JSON.parse(storedData) : null;
    };

    // Load data from sessionStorage on mount
    const storedState = loadFromSessionStorage();

    if (storedState) {
      useStore.setState(transformOnLoad ? transformOnLoad(storedState) : storedState);
    }

    // Save data to sessionStorage before the page unloads
    const saveState = () => {
      const currentState = useStore.getState();
      const stateToSave = transformOnSave ? transformOnSave(currentState) : currentState;

      saveToSessionStorage(stateToSave);
    };

    // Use 'pagehide' for iOS and Safari as 'beforeunload' is unreliable on those platforms
    // https://bugs.webkit.org/show_bug.cgi?id=219102
    const eventName: 'beforeunload' | 'pagehide' =
      devicesCheck.isIOS() || devicesCheck.isSafari() ? 'pagehide' : 'beforeunload';

    window.addEventListener(eventName, saveState);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener(eventName, saveState);
    };
  }, [key, accountId, useStore, transformOnSave, transformOnLoad]);
};
